// HTTP client for the Plumber-served engine API.
//
// The base URL comes from a <meta name="beamm-api-base" content="..."> tag
// embedded by app/main.R so the R side controls the resolved port without
// the JS having to know it at build time.
//
// fetchScenarios() accepts an AbortSignal so callers (Tanstack Query)
// can cancel in-flight requests when the user moves the slider rapidly.
// M1b only fires one scenario per call; the API supports lists so we
// can batch later (compare-reforms in M2).

import { getSessionToken } from "../state/session_token";
import type { Reform, ScenariosRequest, ScenariosResponse } from "./types";

function apiBase(): string {
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="beamm-api-base"]',
  );
  return meta?.content ?? "/api/v1";
}

// JSON content type plus the Shiny session token (once it has arrived) so the
// engine API's request logs correlate with the session that drove them (#194).
function requestHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getSessionToken();
  if (token) headers["X-Beamm-Session"] = token;
  return headers;
}

export async function fetchScenarios(
  reforms: Reform[],
  init?: { signal?: AbortSignal },
): Promise<ScenariosResponse> {
  const body: ScenariosRequest = { reforms };
  const response = await fetch(`${apiBase()}/scenarios`, {
    method: "POST",
    headers: requestHeaders(),
    body: JSON.stringify(body),
    signal: init?.signal,
  });
  if (!response.ok) {
    throw new Error(
      `Scenarios API responded ${response.status} ${response.statusText}`,
    );
  }
  return (await response.json()) as ScenariosResponse;
}

/** Health-check probe — used in dev to confirm Plumber is reachable. */
export async function fetchHealth(): Promise<{
  status: string;
  api_version: string;
}> {
  const response = await fetch(`${apiBase()}/health`);
  if (!response.ok) {
    throw new Error(
      `Health API responded ${response.status} ${response.statusText}`,
    );
  }
  return (await response.json()) as { status: string; api_version: string };
}
